import * as _ from 'lodash';

export function sortSizes(unsortedSizes: { size: string }[], useGtin = true) {
    const sizesSortedByLetterSize = sortSizesByLetterSize(unsortedSizes);
    const unsortedNotLetterAndPantsSizes = useGtin
        ? _.differenceBy(unsortedSizes, sizesSortedByLetterSize, 'gtin')
        : _.difference(unsortedSizes, sizesSortedByLetterSize);
    const unclassifiedSizes = orderNonLetterAndPantsSizes(unsortedNotLetterAndPantsSizes);

    return [...sizesSortedByLetterSize, ...unclassifiedSizes];
}

function sortSizesByLetterSize(sizes: { size: string }[]) {
    return _.reduce(
        letterAndPantsSizes,
        (sizesSortByLetter: { size: string }[], letterSize) => {
            const sizeForLetter = _.find(sizes, ({ size }) => _.toUpper(size) === letterSize);
            if (!sizeForLetter) {
                return sizesSortByLetter;
            }

            return [...sizesSortByLetter, sizeForLetter];
        },
        []
    );
}

function orderNonLetterAndPantsSizes(sizes: { size: string }[]) {
    return [...sizes].sort((currentSize, nextSize) =>
        currentSize.size.localeCompare(nextSize.size, undefined, { numeric: true, sensitivity: 'base' })
    );
}

export function letterSizeComparator(a: string, b: string) {
    const aNumeric = letterAndPantsSizes.indexOf(a);
    const bNumeric = letterAndPantsSizes.indexOf(b);

    return aNumeric - bNumeric;
}

const numberWidthLengthSizes = [
    '11/14',
    '15/16',
    '15/18',
    '17/18',
    '19/20',
    '19/22',
    '23/26',
    '34/28',
    '36/28',
    '38/28',
    '40/28',
    '42/28',
    '44/28',
    '46/28',
    '48/28',
    '24/30',
    '25/30',
    '26/30',
    '27/30',
    '28/30',
    '29/30',
    '30/30',
    '31/30',
    '32/30',
    '33/30',
    '34/30',
    '35/30',
    '36/30',
    '38/30',
    '40/30',
    '42/30',
    '44/30',
    '46/30',
    '48/30',
    '50/30',
    '52/30',
    '54/30',
    '24/32',
    '25/32',
    '26/32',
    '27/32',
    '28/32',
    '29/32',
    '30/32',
    '31/32',
    '32/32',
    '33/32',
    '34/32',
    '35/32',
    '36/32',
    '38/32',
    '40/32',
    '42/32',
    '44/32',
    '46/32',
    '48/32',
    '50/32',
    '52/32',
    '54/32',
    '24/34',
    '25/34',
    '26/34',
    '27/34',
    '28/34',
    '29/34',
    '30/34',
    '31/34',
    '32/34',
    '33/34',
    '34/34',
    '35/34',
    '36/34',
    '38/34',
    '40/34',
    '42/34',
    '44/34',
    '46/34',
    '48/34',
    '50/34',
    '52/34',
    '54/34',
    '24/36',
    '25/36',
    '26/36',
    '27/36',
    '28/36',
    '29/36',
    '30/36',
    '31/36',
    '32/36',
    '33/36',
    '34/36',
    '35/36',
    '36/36',
    '38/36',
    '40/36',
    '42/36',
    '44/36',
    '46/36',
    '48/36',
    '50/36',
    '52/36',
    '54/36',
    '27/38',
    '28/38',
    '29/38',
    '30/38',
    '31/38',
    '32/38',
    '33/38',
    '34/38',
    '35/38',
    '36/38',
    '38/38',
    '40/38',
    '42/38',
    '44/38',
    '46/38',
    '48/38',
    '50/38',
    '52/38',
    '54/38',
    '27/40',
    '28/40',
    '29/40',
    '30/40',
    '31/40',
    '32/40',
    '33/40',
    '34/40',
    '35/40',
    '36/40',
    '38/40',
    '40/40',
    '42/40',
    '44/40',
    '46/40',
    '48/40',
    '50/40',
    '52/40',
    '54/40'
];

export const letterAndPantsSizes: string[] = [
    'OS',
    'One size',
    'XXXS',
    'XXS',
    '2XS',
    'XXS-XS',
    'XXS/XS',
    'XXS/long',
    'XXS/normal',
    'XS-S',
    'XS/S',
    'XS/long',
    'XS/normal',
    'XSS',
    'XS',
    'S',
    'S/long',
    'S/normal',
    'S-M',
    'S/M',
    'SM',
    'M',
    'M-L',
    'M/L',
    'M/long',
    'M/normal',
    'ML',
    'L',
    'L-XL',
    'L/XL',
    'L/long',
    'L/normal',
    'XL',
    'XL/long',
    'XL/normal',
    'XXL',
    '2XL',
    '2XL/long',
    '2XL/normal',
    'XXXL',
    '3XL',
    'XXXXL',
    '4XL',
    'XXXXXL',
    '5XL',
    ..._.flatten(_.range(1, 16).map((size) => generateNumberVariants(size.toString()))),
    ..._.range(16, 35).map((size) => size.toString()),
    '35-38',
    ..._.flatten(_.range(35, 39).map((size) => generateNumberVariants(size.toString()))),
    '39-42',
    ..._.flatten(_.range(39, 43).map((size) => generateNumberVariants(size.toString()))),
    '43-46',
    ..._.flatten(_.range(43, 47).map((size) => generateNumberVariants(size.toString()))),
    '47-50',
    ..._.flatten(_.range(47, 55).map((size) => generateNumberVariants(size.toString()))),
    '92-116',
    '122-146',
    '152-164',
    ...numberWidthLengthSizes,
    ..._.map(numberWidthLengthSizes, (size) => `W${size}`)
];

function generateNumberVariants(size: string) {
    // Takes a number and add some common variants for it, Example: 1 -> [1, 1.5, 1,5, 1_PLUS]
    return [size, `${size}.5`, `${size},5`, `${size}_PLUS`];
}
